import { useEffect, useRef, useState } from 'react';
import {
  IxButton,
  IxContentHeader,
  IxIconButton,
  IxInput,
  IxNumberInput,
  IxPill,
  IxTypography,
} from '@siemens/ix-react';
import { brandButtonStyle } from '../styles/brandButton.js';
import { iconTrashcan, iconInfo } from '@siemens/ix-icons/icons';
import { GaPriceService } from '../api/client.js';
import type { GAPriceDto, GAPriceSearchData } from '../api/client.js';
import {
  buildSearchData,
  validateForm,
  fieldProps,
  toErrorMessage,
  DEFAULT_FORM,
  type CalculateFormState,
} from './calculate.logic.js';
import {
  interpretRow,
  validateBatch,
  summarizeItem,
  MAX_BATCH,
} from './batch.logic.js';
import { openResultDetail, openAlert } from './ResultModals.js';

// A queued request keeps both the built DTO (what we send) and a stable id so
// React keys survive removals without reindex churn.
interface QueuedItem {
  id: number;
  data: GAPriceSearchData;
}

// message only exists on error rows — mirrors interpretRow's discriminated union.
type RowResult =
  | { dto: GAPriceDto; ok: true }
  | { dto: GAPriceDto; ok: false; message: string };

export function Calculate() {
  const [form, setForm] = useState<CalculateFormState>(DEFAULT_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [loading, setLoading] = useState(false);
  const [batchLoading, setBatchLoading] = useState(false);

  // A single request and a batch produce the *same* result rows — a single
  // is just a batch of one. Both feed this one array, rendered as one table.
  const [rows, setRows] = useState<RowResult[] | null>(null);

  // The queue is assembled from single requests ("bulk is nothing than multiple
  // single requests all together").
  const [queue, setQueue] = useState<QueuedItem[]>([]);
  const nextId = useRef(1);

  // Results render below the tall form. IxContent is a fixed-height, internally
  // -scrolling container, so freshly-rendered results can land below the fold —
  // scroll them into view whenever they change. (App-level errors go to a modal
  // overlay instead, which can never be hidden below the fold.)
  const resultsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (rows) {
      resultsRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [rows]);

  function setField(key: keyof CalculateFormState, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  }

  /** Validates the form and returns the built DTO, or null if invalid. */
  function validateAndBuild(): GAPriceSearchData | null {
    const errs = validateForm(form);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return null;
    }
    setErrors({});
    return buildSearchData(form);
  }

  async function handleSingle(e: React.FormEvent) {
    e.preventDefault();
    setRows(null);

    const dto = validateAndBuild();
    if (!dto) {
      return;
    }

    setLoading(true);
    try {
      setRows([toRowResult(await GaPriceService.createCalculation(dto))]);
    } catch (err) {
      openAlert('Request failed', toErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  function handleAddToBatch() {
    if (queue.length >= MAX_BATCH) {
      return;
    }
    const dto = validateAndBuild();
    if (!dto) {
      return;
    }
    // Clear stale output so the queue is the visible focus.
    setRows(null);
    const id = nextId.current;
    nextId.current += 1;
    setQueue((prev) => [...prev, { id, data: dto }]);
  }

  function removeFromQueue(id: number) {
    setQueue((prev) => prev.filter((q) => q.id !== id));
  }

  function clearQueue() {
    setQueue([]);
    setRows(null);
  }

  async function handleBatch() {
    setRows(null);

    const items = queue.map((q) => q.data);
    const check = validateBatch(items);
    if (!check.ok) {
      openAlert('Cannot run batch', check.error);
      return;
    }

    setBatchLoading(true);
    try {
      const dtos = await GaPriceService.createCalculationBatch(items);
      setRows(dtos.map(toRowResult));
    } catch (err) {
      openAlert('Batch failed', toErrorMessage(err));
    } finally {
      setBatchLoading(false);
    }
  }

  const fp = (key: keyof CalculateFormState) => fieldProps(errors, key);
  const atCap = queue.length >= MAX_BATCH;

  return (
    <div style={{ padding: '1.25rem 1.5rem', maxWidth: '920px', margin: '0 auto' }}>
      <IxContentHeader headerTitle="GA Price Calculator" style={{ marginBottom: '0.25rem' }} />
      <IxTypography format="body-sm" color="soft" style={{ marginBottom: '1rem', display: 'block' }}>
        Fill in a request and <strong>Calculate</strong> it directly, or{' '}
        <strong>Add to batch</strong> to queue several requests and run them together
        (up to {MAX_BATCH}). Every result row is clickable — open it for the full detail
        and to copy it.
      </IxTypography>

      {/* ── Request form ────────────────────────────────────────────── */}
      <form onSubmit={handleSingle} noValidate>
        <IxTypography format="h4" style={{ marginBottom: '0.5rem' }}>Request</IxTypography>
        <div style={GRID}>
          <IxInput
            label="Business Type"
            placeholder="e.g. N"
            required
            value={form.BusinessType}
            onValueChange={(e: CustomEvent<string>) => setField('BusinessType', e.detail)}
            {...fp('BusinessType')}
          />
          <IxNumberInput
            label="Order Quantity"
            placeholder="e.g. 1"
            required
            value={Number(form.OrderQuantity)}
            min={1}
            step={1}
            onValueChange={(e: CustomEvent<number>) =>
              setField('OrderQuantity', String(Number.isFinite(e.detail) ? e.detail : ''))
            }
            {...fp('OrderQuantity')}
          />
          <IxNumberInput
            label="L1 Price"
            placeholder="e.g. 31271"
            required
            value={Number(form.L1Price)}
            min={0.000001}
            onValueChange={(e: CustomEvent<number>) =>
              setField('L1Price', String(Number.isFinite(e.detail) ? e.detail : ''))
            }
            {...fp('L1Price')}
          />
          <IxInput
            label="Request ID (optional)"
            placeholder="Correlation ID"
            value={form.RequestId}
            onValueChange={(e: CustomEvent<string>) => setField('RequestId', e.detail)}
          />
        </div>

        <IxTypography format="h4" style={{ margin: '1rem 0 0.5rem' }}>Product Identification</IxTypography>
        <div style={GRID}>
          <IxInput
            label="MLFB"
            placeholder="e.g. 3AH4305-2ME40-0ED2-Z"
            required
            value={form.Mlfb}
            onValueChange={(e: CustomEvent<string>) => setField('Mlfb', e.detail)}
            {...fp('Mlfb')}
          />
          <IxInput
            label="Price Group"
            placeholder="e.g. 8W1"
            required
            value={form.PriceGroup}
            onValueChange={(e: CustomEvent<string>) => setField('PriceGroup', e.detail)}
            {...fp('PriceGroup')}
          />
          <IxInput
            label="PCK Vendor"
            placeholder="e.g. 4715"
            required
            value={form.PCKVendor}
            onValueChange={(e: CustomEvent<string>) => setField('PCKVendor', e.detail)}
            {...fp('PCKVendor')}
          />
          <IxInput
            label="GCK Vendor"
            placeholder="e.g. R3P5"
            required
            value={form.GCKVendor}
            onValueChange={(e: CustomEvent<string>) => setField('GCKVendor', e.detail)}
            {...fp('GCKVendor')}
          />
          <IxInput
            label="List Recognition Mark"
            placeholder="(blank = none)"
            value={form.ListRecognitionMark}
            onValueChange={(e: CustomEvent<string>) => setField('ListRecognitionMark', e.detail)}
          />
        </div>

        <IxTypography format="h4" style={{ margin: '1rem 0 0.5rem' }}>Customer Identification</IxTypography>
        <div style={GRID}>
          <IxInput
            label="Country Code"
            placeholder="e.g. SE"
            required
            value={form.CountryCode}
            onValueChange={(e: CustomEvent<string>) => setField('CountryCode', e.detail)}
            {...fp('CountryCode')}
          />
          <IxInput
            label="Org ID Buyer"
            placeholder="e.g. A1201456"
            required
            value={form.OrgIdBuyer}
            onValueChange={(e: CustomEvent<string>) => setField('OrgIdBuyer', e.detail)}
            {...fp('OrgIdBuyer')}
          />
          <IxInput
            label="Project Number"
            placeholder="(optional)"
            value={form.ProjectNumber}
            onValueChange={(e: CustomEvent<string>) => setField('ProjectNumber', e.detail)}
          />
          <IxInput
            label="Customer Number"
            placeholder="(optional)"
            value={form.CustomerNumber}
            onValueChange={(e: CustomEvent<string>) => setField('CustomerNumber', e.detail)}
          />
          <IxInput
            label="Customer Group"
            placeholder="(optional)"
            value={form.CustomerGroup}
            onValueChange={(e: CustomEvent<string>) => setField('CustomerGroup', e.detail)}
          />
          <IxInput
            label="GCK Buyer"
            placeholder="(optional)"
            value={form.GCKBuyer}
            onValueChange={(e: CustomEvent<string>) => setField('GCKBuyer', e.detail)}
          />
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', margin: '1rem 0', flexWrap: 'wrap' }}>
          <IxButton type="submit" style={brandButtonStyle} disabled={loading}>
            {loading ? 'Calculating…' : 'Calculate'}
          </IxButton>
          <IxButton
            type="button"
            variant="secondary"
            style={brandButtonStyle}
            disabled={atCap}
            onClick={handleAddToBatch}
          >
            Add to batch
          </IxButton>
          {atCap && (
            <IxTypography format="body-sm" color="soft" style={{ alignSelf: 'center' }}>
              Batch is full ({MAX_BATCH} max).
            </IxTypography>
          )}
        </div>
      </form>

      {/* ── Batch queue ─────────────────────────────────────────────── */}
      {queue.length > 0 && (
        <section style={{ marginTop: '0.5rem' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={TABLE}>
              <caption style={CAPTION}>Batch queue — {queue.length} request{queue.length !== 1 ? 's' : ''}</caption>
              <thead>
                <tr>
                  <th scope="col" style={TH}>Request ID</th>
                  <th scope="col" style={TH}>MLFB</th>
                  <th scope="col" style={TH}>Qty</th>
                  <th scope="col" style={TH}>L1 Price</th>
                  <th scope="col" style={TH}>Country</th>
                  <th scope="col" style={TH}><span style={SR_ONLY}>Remove</span></th>
                </tr>
              </thead>
              <tbody>
                {queue.map((q, idx) => {
                  const s = summarizeItem(q.data);
                  return (
                    <tr key={q.id} data-testid={`queue-row-${idx}`}>
                      <td style={TD}>{idx + 1}</td>
                      <td style={TD}>{s.mlfb}</td>
                      <td style={TD}>{s.qty}</td>
                      <td style={TD}>{s.l1Price}</td>
                      <td style={TD}>{s.country}</td>
                      <td style={{ ...TD, textAlign: 'right' }}>
                        <IxIconButton
                          icon={iconTrashcan}
                          variant="tertiary"
                          size="16"
                          aria-label={`Remove request ${idx + 1} from batch`}
                          onClick={() => removeFromQueue(q.id)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', margin: '0.75rem 0', flexWrap: 'wrap' }}>
            <IxButton type="button" style={brandButtonStyle} disabled={batchLoading} onClick={handleBatch}>
              {batchLoading ? 'Calculating…' : `Calculate batch (${queue.length})`}
            </IxButton>
            <IxButton type="button" variant="tertiary" disabled={batchLoading} onClick={clearQueue}>
              Clear
            </IxButton>
          </div>
        </section>
      )}

      {/* ── Results (single = one row, batch = many; identical UI) ───── */}
      {rows && rows.length > 0 && (
        <div ref={resultsRef} style={{ overflowX: 'auto', marginTop: '0.5rem' }}>
          <table style={TABLE}>
            <caption style={CAPTION}>
              {rows.length === 1 ? 'Result' : `Results — ${rows.length} rows`}
              <span style={{ fontWeight: 400, marginLeft: '0.5rem' }}>
                <IxTypography format="body-sm" color="soft" style={{ display: 'inline' }}>
                  (click a row for full detail)
                </IxTypography>
              </span>
            </caption>
            <thead>
              <tr>
                <th scope="col" style={TH}>Request ID</th>
                <th scope="col" style={TH}>Id</th>
                <th scope="col" style={TH}>Status</th>
                <th scope="col" style={TH}>GA Price</th>
                <th scope="col" style={TH}>Currency</th>
                <th scope="col" style={TH}>Message</th>
                <th scope="col" style={TH}><span style={SR_ONLY}>Details</span></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((rowItem, idx) => (
                <ResultRow
                  key={rowItem.ok && rowItem.dto.Id ? rowItem.dto.Id : `row-${idx}`}
                  rowItem={rowItem}
                  idx={idx}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Maps a result DTO to a row, tagging the logical outcome (200 = ok). */
function toRowResult(dto: GAPriceDto): RowResult {
  const interp = interpretRow(dto);
  return interp.ok ? { dto, ok: true } : { dto, ok: false, message: interp.message };
}

/**
 * One result table row. Extracted out of the inline .map callback — the callback
 * now returns a single element, keeping Sonar's complexity on the map low while
 * this component owns the per-row branching (error styling, message, key).
 */
function ResultRow({ rowItem, idx }: { rowItem: RowResult; idx: number }) {
  const isError = !rowItem.ok;
  const message = rowItem.ok ? (rowItem.dto.ResponseMessage ?? '') : rowItem.message;
  const open = () => openResultDetail(rowItem.dto, idx, isError ? message : null);
  return (
    <tr
      data-testid={`result-row-${idx}`}
      onClick={open}
      style={{
        cursor: 'pointer',
        backgroundColor: isError
          ? 'var(--theme-color-alarm--20, rgba(255,80,80,0.12))'
          : undefined,
      }}
    >
      <td style={TD}>{idx + 1}</td>
      <td style={TD}>{rowItem.dto.Id ?? '—'}</td>
      <td style={TD} data-testid={`result-row-${idx}-status`}>
        <IxPill variant={isError ? 'alarm' : 'success'}>
          {rowItem.dto.ResponseStatus ?? '—'}
        </IxPill>
      </td>
      <td style={TD}>{rowItem.dto.GaPrice ?? '—'}</td>
      <td style={TD}>{rowItem.dto.Currency ?? '—'}</td>
      <td style={{ ...TD, color: isError ? 'var(--theme-color-alarm, #e00)' : undefined }}>
        {message}
      </td>
      <td style={{ ...TD, textAlign: 'right' }}>
        <IxIconButton
          icon={iconInfo}
          variant="tertiary"
          size="16"
          aria-label={`View details for result ${idx + 1}`}
          onClick={(e) => {
            e.stopPropagation();
            open();
          }}
        />
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Shared styles
// ---------------------------------------------------------------------------

const GRID: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '0.75rem',
};

const TABLE: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.875rem',
};

const CAPTION: React.CSSProperties = {
  textAlign: 'left',
  marginBottom: '0.5rem',
  fontWeight: 600,
};

const TH: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.5rem 0.75rem',
  borderBottom: '2px solid var(--theme-color-ghost--20, rgba(128,128,128,0.25))',
  whiteSpace: 'nowrap',
};

const TD: React.CSSProperties = {
  padding: '0.4rem 0.75rem',
  borderBottom: '1px solid var(--theme-color-ghost--10, rgba(128,128,128,0.15))',
  verticalAlign: 'middle',
};

const SR_ONLY: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0,0,0,0)',
  whiteSpace: 'nowrap',
  border: 0,
};
